import React, { useContext, useState, useRef, useEffect } from 'react'
import { DAGInput } from '~/types/queenbee/io'
import { Node, Queenbee } from '~/types'
import { graphics } from '~/utils'
import { context } from '~/state/detailContext'

export interface RecipeInputNodeProps extends Node.Props {
  input: DAGInput
}

export const RecipeInputNode: React.FunctionComponent<RecipeInputNodeProps> = ({ position, label, input }) => {
  const { reference, setNodeReference } = useContext(context)
  const [cardIsOpen, setCardIsOpen] = useState(false)
  const nodeRef = useRef<SVGCircleElement>(null)

  const [x, y] = position
  const { required, annotations } = input

  const type = (annotations?.type ?? 'string') as Queenbee.InputType
  const icon = graphics.getInputIcon(type)

  useEffect(() => {
    if (!reference || reference.name !== label) {
      setCardIsOpen(false)
    }
  }, [reference, setCardIsOpen, label])

  const handlePointerEnter = (): void => {
    const target = nodeRef.current

    if (!target) {
      return
    }

    const { left, top } = target.getBoundingClientRect()

    const position: [number, number] = [left + 17, top + 17]

    setNodeReference({ type: 'input', name: input.name, position: position, input: input })
    setCardIsOpen(true)
  }

  const handlePointerLeave = (): void => {
    setNodeReference(undefined)
  }

  return (
    <g transform={`translate(${x - 4}, ${y})`} style={{ opacity: cardIsOpen ? 0.01 : 1 }}>
      <g transform="translate(-13, -13)">
        <g className="input-node" onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
          <circle cx={13} cy={13} r={17} stroke="#333" strokeWidth="2px" fill="#FFF" ref={nodeRef} />
          <polyline
            points="13,-4 30,-4 30,13"
            stroke="#333"
            strokeWidth="2px"
            fill="#FFF"
            transform="rotate(45, 13, 13)"
          />
          <g className={`input-node-icon`}>{icon}</g>
          {cardIsOpen ? null : (
            <g transform={`translate(${required ? '-25' : '-11'}, 16)`}>
              <text className="recipe-input-node-label" textAnchor="end" fontSize={12}>
                <tspan>{label.length > 12 ? `${label.substr(0, 12)}...` : label}</tspan>
              </text>
            </g>
          )}
        </g>
        {required && !cardIsOpen ? (
          <g transform="scale(1.5) translate(-10, 16)">
            <text textAnchor="middle" className="recipe-input-node-required-label">
              <tspan>*</tspan>
            </text>
          </g>
        ) : null}
      </g>
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter&display=swap');

        .input-node {
          cursor: pointer;
        }

        .input-node-icon {
          opacity: 1;
          transform: translate(7px, 7px);
          transition: 300ms;
          transition-timing-function: ease;
        }

        .input-node-download-icon {
          opacity: 0;
          transform: translate(7px, -5px);
          transition: 300ms;
          transition-timing-function: ease;
        }

        .input-node:hover > .input-node-icon--live {
          opacity: 0;
          transform: translate(7px, 19px);
        }

        .input-node:hover > .input-node-download-icon--live {
          opacity: 1;
          transform: translate(7px, 7px);
        }

        .recipe-input-node-label {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 400;
          fill: #333;
          pointer-events: none;
          user-select: none;
        }
      `}</style>
    </g>
  )
}

export default RecipeInputNode
